import { get, times } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { getPeriod, getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { getContentSets } from "selectors/contentSets"
import { computeSecuritiesQueries, computeSecuritiesDescriptorsQueries, getCoveragePositionSetQuery } from "helpers/comparison"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as ResearchTopicRootCreators } from "modules/research-topic-root/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators } from "./model"
import { getComparisonTypes, getSearchItemsActions, getSavedMetrics, getSavedCompositionGroup, getSavedTopicSummaryGroup, getBaseUrl, getSummaryPanelOpen, getSelectedExperiment, getSelectedSegment, getExperimentToUpdate, getHedgeItem, getHedges, getModelName } from "./selectors"
import { getInstrumentDescriptors, getInstrument } from "./selectors/instrument"
import { getBaseItems, getComparisonItems, getInstrumentPortfolioPositions, getInstrumentPortfolio } from "./selectors/items"
import { getSelectedComparisonType, getCurrentTopicId, getExperimentPositionSet } from "./selectors/model"
import { getComparisonSecurities } from "./selectors/availability"
import { getStartDate, getEndDate, getLatestDate, getEarliestDate, getSelectedDate, getNonOverlappingItems, getTopicSummaryDate } from "./selectors/dates"
import { ASSETS_SUBQUERY } from "./helpers"
import withOutletContext from "hocs/withOutletContext"
import { getTopicFileName } from "modules/comparison/selectors"
import { BASKETS_QUERY } from "resources/baskets"

export default withOutletContext(createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    portfolio: state.global.selected.portfolio,
    modelId: get(props, "modelId"),
    comparisonSecurities: getComparisonSecurities(state, props),
    baseItems: getBaseItems(state),
    comparisonItems: getComparisonItems(state, props),
    currentTopicId: getCurrentTopicId(state, props),
    instrument: getInstrument(state, props),
    startDate: getStartDate(state, props),
    endDate: getEndDate(state, props),
    selectedExperiment: getSelectedExperiment(state),
    researchTopicDate: getResearchTopicDate(state, props),
    fileName: getTopicFileName(state, props),
  }),
  required: ["endDate", "instrument"],
  data: [{
    resource: "benchmarksAndPortfolios",
    as: null,
    url: ({ modelId }) => {
      if (modelId) {
        return ({
          id: `benchmarksAndPortfolios-${modelId}`,
          query: `query ($modelId: String!) {
            model (id: $modelId) {
              benchmarks { id, name, availableFrom }
            }
            portfolios { id, name, availableFrom }
          }`,
          variables: { modelId },
        })
      } else {
        return null
      }
    },
  }, {
    resource: "instrumentAvailability",
    url: ({ modelId, instrument }) => {
      if (!modelId || instrument.portfolioId) { return null }
      return ({
        id: `instrument-${modelId}-${JSON.stringify(instrument.securityId)}`,
        query: `query ($modelId: String!, $ticker: String, $mic: String,  $internalId: String) {
            model (id: $modelId) {
              security(ticker: $ticker, mic: $mic, internalId: $internalId) {
                availability {
                  from
                  to
                }
              }
            }
          }`,
        variables: {
          modelId,
          ticker: instrument.securityId.ticker,
          mic: instrument.securityId.mic,
          internalId: instrument.securityId.internalId,
        },
      })
    },
  }, {
    resource: "instrumentDescriptors",
    url: ({ modelId, instrument, researchTopicDate }) => {
      if (!modelId || !researchTopicDate || (instrument && (instrument.portfolioId || !instrument.securityId))) { return null }
      return ({
        id: `instrument-${modelId}-${researchTopicDate}-${JSON.stringify(instrument.securityId)}`,
        query: `query ($modelId: String!, $on: Date!, $ticker: String, $mic: String, $internalId: String) {
            model (id: $modelId) {
              security(ticker: $ticker, mic: $mic, internalId: $internalId) {
                descriptors (on: $on) {
                  ticker
                  mic
                  name
                  assetClass
                  modelProviderId
                  bloombergTicker
                  internalId
                  figi
                }
              }
            }
          }`,
        variables: {
          modelId,
          on: researchTopicDate,
          ticker: instrument.securityId.ticker,
          mic: instrument.securityId.mic,
          internalId: instrument.securityId.internalId,
        },
      })
    },
  },
  {
    resource: "instrumentPortfolio",
    url: ({ modelId, instrument, researchTopicDate }) => {
      if (!modelId || !researchTopicDate || !instrument.portfolioId) { return null }
      return ({
        id: `instrument-${modelId}-${researchTopicDate}-${JSON.stringify(instrument.portfolioId)}`,
        query: `query ($portfolioId: String!, $modelId: String!, $to: Date!, $from: Date!) {
          portfolio(id: $portfolioId) {
            id
            name
            description
            alias
            availableFrom
            fxHedgeEnabled
            dates(to: $to, from: $from, modelId: $modelId) {
              gmv
              equity
              ${ASSETS_SUBQUERY}
            }
          }
        }`,
        variables: {
          modelId,
          portfolioId: instrument.portfolioId,
          from: researchTopicDate,
          to: researchTopicDate,
        },
      })
    },
  },
  {
    resource: "instrumentPortfolioModelMeta",
    url: ({ account, instrument, modelId }) => {
      if (!modelId || !instrument.portfolioId) { return null }
      return `/api/${account}/m/${modelId}/r/portfolio/${instrument.portfolioId}`
    },
  },
  {
    ...BASKETS_QUERY,
    as: null,
  }, {
    resource: "securities",
    as: null,
    passthrough: true,
    url: ({ modelId, comparisonSecurities }) => {
      if (modelId && comparisonSecurities.length > 0) {
        const securitiesQueries = computeSecuritiesQueries(comparisonSecurities)
        return ({
          id: `securities-${modelId}-${securitiesQueries}`,
          query: `query ($modelId: String!) {
            model (id: $modelId) {
              ${securitiesQueries}
            }
          }`,
          variables: { modelId },
        })
      } else {
        return null
      }
    },
  }, {
    resource: "securitiesDescriptors",
    as: null,
    passthrough: true,
    url: ({ modelId, comparisonSecurities, endDate }) => {
      if (modelId && comparisonSecurities.length > 0) {
        const securitiesQueries = computeSecuritiesDescriptorsQueries(comparisonSecurities)
        return ({
          id: `securities-${modelId}-${endDate}-${securitiesQueries}`,
          query: `query ($modelId: String!, $on: Date!) {
            model (id: $modelId) {
              ${securitiesQueries}
            }
          }`,
          variables: { modelId, on: endDate },
        })
      } else {
        return null
      }
    },
  }, {
    resource: "researchTopicPreferences",
    url: ({ accountId, currentTopicId }) => {
      if (!currentTopicId) {
        return null
      }

      return ({
        id: `researchTopicPreferences-${accountId}${currentTopicId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "researchTopicSettings",
              key: currentTopicId,
              accountId,
            },
          ],
        },
      })
    },
  }, {
    resource: "modeledDates",
    url: ({ modelId, startDate, endDate }) => {
      if (!modelId || !startDate || !endDate) {
        return null
      }

      return {
        id: `${modelId}${endDate}${startDate}`,
        query: `query ($modelId: String!, $from: Date!, $to: Date!) {
          model (id: $modelId) {
            availability {
              dates(from: $from, to: $to)
            }
          }
        }`,
        variables: { modelId, from: startDate, to: endDate },
      }
    },
  },
  ...times(4, (n) => {
    return {
      resource: `topicComparisonItemCoverage${n + 1}`, // To match the other queries by items, we don't get the base instrument here
      as: null,
      url: ({ modelId, baseItems, startDate, endDate, currentTopicId }) => {
        if (!baseItems[n]) {
          return null
        }

        const positionSet = getCoveragePositionSetQuery(baseItems[n], startDate, currentTopicId)

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${startDate}${endDate}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!) {
              model (id: $modelId) {
                simulation (positionSet: $positionSet,from: $from, to: $to) {
                  coverage{
                    date
                    summary{
                      percentGmvAvailable
                    }
                  }
                }
              }
            }`,
          variables: {
            modelId,
            positionSet,
            from: startDate,
            to: endDate,
          },
        }
      },
    }
  })],
  mapStateToProps: (state, props) => ({
    comparisonItems: getComparisonItems(state, props),
    selectedComparisonType: getSelectedComparisonType(state, props),
    searchItemsActions: getSearchItemsActions(state, props),
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    period: getPeriod(state),
    nonOverlappingItems: getNonOverlappingItems(state, props),
    savedMetrics: getSavedMetrics(state),
    savedCompositionGroup: getSavedCompositionGroup(state),
    savedTopicSummaryGroup: getSavedTopicSummaryGroup(state),
    comparisonTypes: getComparisonTypes(state),
    topicSummaryDate: getTopicSummaryDate(state, props),
    baseUrl: getBaseUrl(state, props),
    contentSets: getContentSets(state),
    summaryPanelOpen: getSummaryPanelOpen(state),
    instrumentDescriptors: getInstrumentDescriptors(state),
    experimentPositionSet: getExperimentPositionSet(state),
    experimentToUpdate: getExperimentToUpdate(state),
    selectedDate: getSelectedDate(state, props),
    hedgeItem: getHedgeItem(state),
    currentHedges: getHedges(state),
    modelName: getModelName(state),
    selectedSegment: getSelectedSegment(state, props),
    instrumentPortfolio: getInstrumentPortfolio(state, props),
    instrumentPortfolioPositions: getInstrumentPortfolioPositions(state, props),
  }),
  actions: {
    ...Creators,
    setSelectedDate: ResearchTopicRootCreators.setSelectedDate,
    setPeriod: ResearchTopicRootCreators.setPeriod,
    resetSelectedDate: ResearchTopicRootCreators.resetSelectedDate,
    showNotification: NotificationCreators.add,
    openDialog: DialogCreators.open,
    closeDialog: DialogCreators.close,
  },
}))
